import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import classnames from 'classnames';
import Logo from './components/Logo';
import LinkSelector from '../../../Header/LinkSelector';
import { Offcanvas } from '../../../Offcanvas/Offcanvas';
import { CategoryLink } from '../../../form-elements/Link';
import Button from '../../../form-elements/Button';
import { categoriesSelector } from '../../../../../modules/selectors/standort/categories';
import { categoryUrlSelector, standortUrlSelector } from '../../../../../modules/selectors/url';
import ShopMenuList from '../../../Header/ShopMenuList';
import { carDealerLinkSelector, heroContentByPageSelector } from '../../../../../modules/selectors/standort/theme';
import { pageSelector } from '../../../../../modules/selectors/url';
import loadable from '@loadable/component';
import { markenStandortSelector } from '../../../../../modules/selectors/standort/markenStandort';
import Image, { useFallbackImageSrc } from '../../../Image/Image';
import { saveCouponBannerHeight } from '../../../../../modules/actions/couponBannerHeight';
import useWindowSize from '../../../../../helpers/windowHelper';
import { apiClient } from '../../../../../helpers/apiHelperV2';
import useSWR from 'swr';
import { setServiceNavigatorStatus } from '../../../../../modules/actions/standort/service/serviceNavigator';
import { MobileMenu } from '../../../MobileMenu/MobileMenu';

const CouponBanner = loadable(() => import('../../../CouponBanner/Couponbanner'));

function GottfriedHeader({ links }) {
    const categories = useSelector(categoriesSelector);
    const categoryUrl = useSelector(categoryUrlSelector);
    const heroContentByPage = useSelector(heroContentByPageSelector);
    const page = useSelector(pageSelector);
    const carDealerLink = useSelector(carDealerLinkSelector);
    const [isNavSidebarVisible, setIsNavSidebarVisible] = useState(false);
    const focusBtnRef = useRef();

    const markenStandort = useSelector(markenStandortSelector);
    const [mobileTeaserSrc, mobileTeaserOnError] = useFallbackImageSrc({
        src: `https://techno-images.s3.eu-central-1.amazonaws.com/standort-images/${markenStandort.gesellschafterNr}/${markenStandort.standortNr}/MobileTeaserImg.jpg`,
        fallbackSrc: 'https://techno-images.s3.eu-central-1.amazonaws.com/national/teaserbild_m1.jpg',
    });
    // must be in sync with $breakpoint-medium
    const BREAKPOINT_MEDIUM = 960;
    const [couponBannerActive, setCouponBannerActive] = useState(false)
    const standortUrl = useSelector(standortUrlSelector)
    const coupons = useSelector(state => state.standort[standortUrl].coupons)

    const checkIfCouponBannerActive = () => {
        return coupons?.data.some(coupon => { return coupon.banner.isActive === true })
    }
    const dispatch = useDispatch();

    const { width } = useWindowSize();
    const BREAKPOINT_SMALL = 640

    useEffect(() => {
        let isActive = checkIfCouponBannerActive()
        setCouponBannerActive(isActive)
        dispatch(saveCouponBannerHeight(isActive ? 36 : 0))
    }, [coupons]);

    useEffect(() => {
        if (isNavSidebarVisible && focusBtnRef.current) {
            focusBtnRef.current.focus();
        }
    }, [isNavSidebarVisible]);

    const determineHeight = useCallback((width) => {
        let height = 0
        height += width > BREAKPOINT_MEDIUM ? 70 : 30
        height += couponBannerActive ? 36 : 0
        return height
    })

    const closeSidebar = useCallback(() => {
        setIsNavSidebarVisible(false);
    }, []);

    const openSidebar = useCallback(() => {
        setIsNavSidebarVisible(true);
    }, []);

    const { data: serviceNavigatorstatus } = useSWR(`/api/v1/getServicenavigatorStatus?standortId=${markenStandort._id}`, async url => {
        const { data } = await apiClient.get(url);
        return data;
    });

    const serviceNavigatorStatus = useMemo(() => serviceNavigatorstatus?.status ?? [], [serviceNavigatorstatus]);

    if (serviceNavigatorStatus !== undefined) {
        dispatch(setServiceNavigatorStatus(serviceNavigatorStatus));
    }

    const filterSpecialCategories = useCallback((categoryList) => {
        if (!categoryList) return [];
        
        return categoryList
            .filter(cat => ('isSpecial' in cat && !cat.isSpecial) || !('isSpecial' in cat))
            .map(cat => {
                if (cat.subCategories && cat.subCategories.length > 0) {
                    return {
                        ...cat,
                        subCategories: filterSpecialCategories(cat.subCategories)
                    };
                }
                return cat;
            });
    }, []);
    
    const filteredCategories = useMemo(() => {
        if (!categories) return [];
        if('showSpecialCategories' in markenStandort && !markenStandort.showSpecialCategories) {
            return categories;
        }
        return filterSpecialCategories(categories);
    }, [categories, markenStandort, filterSpecialCategories]);

    const { reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung } = useMemo(() => {
        if (!serviceNavigatorStatus) {
            const [reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung] = _.partition(filteredCategories, { url: 'reparatur-wartung' });
            return { reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung };
        } else {
            const [reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung] = _.partition(filteredCategories, { url: '' });
            return { reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung };
        }

    }, [filteredCategories, serviceNavigatorStatus]);

    const isLinkActive = category => {
        return category.url === categoryUrl || category.subCategories.find(subCategory => subCategory.url === categoryUrl);
    };

    const heroContent = useMemo(() => {
        return heroContentByPage.filter(hero => hero.page === page);
    }, [page, heroContentByPage]);

    let handlePress = (e) => {
        e.preventDefault()
        if (e.key == "Enter") {
            closeSidebar()
        }
    }

    return (
        <>
            <div className="uk-sticky-fixed uk-width-1-1" style={{ height: determineHeight(width), position: 'sticky', top: 0 }}>
                <div className="uk-navbar-container uk-navbar-sticky">
                    <div className="uk-container">
                        <div className="uk-flex uk-flex-middle uk-flex-between">
                            <div className="gottfried_header_logos">
                                <Logo />
                                <Image
                                    src={`https://techno-images.s3.eu-central-1.amazonaws.com/standort-images/${markenStandort.gesellschafterNr}/${markenStandort.standortNr}/AllBrands.png`}
                                    alt={`${markenStandort.title} BrandsLogo`}
                                    resizeHeight={45}
                                />
                            </div>
                            <nav className="uk-navbar" data-uk-navbar="" aria-label='Hauptmenü'>
                                <div className="uk-navbar-right">
                                    <Button
                                        className="uk-navbar-item uk-text-primary uk-text-bold"
                                        modifier="link"
                                        onClick={openSidebar}
                                        ariaLabel="Menü öffnen"
                                    >
                                        <span className="uk-flex uk-flex-column">
                                            <i data-uk-navbar-toggle-icon="ratio: 1.7" aria-hidden="true" />
                                            <span className="uk-text-uppercase uk-text-normal uk-text-small">Menü</span>
                                        </span>
                                    </Button>
                                </div>
                            </nav>
                        </div>
                    </div>
                </div>

                <CouponBanner />
            </div>
            <Offcanvas isVisible={width && width <= BREAKPOINT_SMALL && isNavSidebarVisible} onIsVisibleChange={visible => setIsNavSidebarVisible(visible)}>
                <div className="uk-offcanvas-bar mobile-full-modal">
                    <div style={{padding: 15}}>
                        <button className="uk-offcanvas-close focus" type="button" ref={focusBtnRef} data-uk-close="" onKeyPress={handlePress} onClick={closeSidebar} aria-label='Navigation schließen' />
                        <span className='uk-text-bold menu-black'><strong style={{fontWeight: "bold !important"}}>Menü</strong></span>
                    </div>
                    <MobileMenu markenStandort={markenStandort} categories={[...categoriesWithoutReparaturUndWartung, ...reparaturUndWartungCategory]} setIsNavSidebarVisible={setIsNavSidebarVisible}>                
                        <>
                            
                            {serviceNavigatorStatus && (
                                <li className="mobile-menu-category" onClick={closeSidebar}>
                                    <strong>
                                        <LinkSelector
                                            to='/servicenavigator'
                                            label='Service-Navigator'
                                            className="uk-text-bold strong-text"
                                            type='internalLink'
                                        />
                                    </strong>
                                </li>
                            )}
                            {!_.isEmpty(links) && (
                                <>
                                    <li className="mobile-menu-category">
                                        <LinkSelector to={carDealerLink.to} type={carDealerLink.type} className="strong-text" label={<h2><strong>{carDealerLink.label}</strong></h2>} />
                                    </li>
                                    {links.map((item, index) => (
                                        <li className="mobile-menu-category" key={index}>
                                            <strong>
                                                <LinkSelector
                                                    to={item.to}
                                                    action={item.action}
                                                    category={item.category}
                                                    label={item.label}
                                                    type={item.type}
                                                    className="strong-text"
                                                />
                                            </strong>
                                        </li>
                                    ))}
                                </>
                            )} 
                        </>
                    </MobileMenu>
                    
                </div>
                
            </Offcanvas>
            <Offcanvas overlay isVisible={width && width > BREAKPOINT_SMALL && isNavSidebarVisible} onIsVisibleChange={visible => setIsNavSidebarVisible(visible)} flip={true}>
                <div className="uk-offcanvas-bar">
                    <button className="uk-offcanvas-close focus" type="button" ref={focusBtnRef} data-uk-close="" onKeyPress={handlePress} onClick={closeSidebar} aria-label='Navigation schließen' />
                    <span className='uk-text-bold menu-black'><strong style={{ 'font-weight': "bold !important" }}>Menü</strong></span>
                    <ShopMenuList categories={categoriesWithoutReparaturUndWartung} closeSidebar={closeSidebar} data-uk-nav="" />
                    <ul className="uk-nav-default uk-nav-parent-icon uk-margin-top" data-uk-nav="">
                        <li onClick={closeSidebar}>
                            <strong>
                                <LinkSelector
                                    to='/audi-zubehoer'
                                    label='Audi-Zubehör'
                                    className="uk-text-bold strong-text"
                                    type='internalLink'
                                />
                            </strong>
                        </li>
                    </ul>
                    <ul className="uk-nav-default uk-nav-parent-icon uk-margin-top" data-uk-nav="">
                        {reparaturUndWartungCategory.map(category => (
                            <li key={category._id} className={classnames({ 'uk-active': isLinkActive(category) })} onClick={closeSidebar}>
                                <strong>
                                    <CategoryLink category={category} className="uk-text-bold strong-text">
                                        {category.title}
                                    </CategoryLink>
                                </strong>
                            </li>
                        ))}
                    </ul>
                    {serviceNavigatorStatus && (
                        <ul className="uk-nav-default uk-nav-parent-icon uk-margin-top" data-uk-nav="">
                            <li onClick={closeSidebar}>
                                <strong>
                                    <LinkSelector
                                        to='/servicenavigator'
                                        label='Service-Navigator'
                                        className="uk-text-bold strong-text"
                                        type='internalLink'
                                    />
                                </strong>
                            </li>
                        </ul>
                    )}
                    {!_.isEmpty(links) && (
                        <div className="uk-margin-large-top">
                            <LinkSelector to={carDealerLink.to} type={carDealerLink.type} className="strong-text" label={<h2><strong>{carDealerLink.label}</strong></h2>} />
                            <ul className="uk-nav-default uk-nav-parent-icon" data-uk-nav="">
                                {links.map((item, index) => (
                                    <li key={index}>
                                        <strong>
                                            <LinkSelector
                                                to={item.to}
                                                action={item.action}
                                                category={item.category}
                                                label={item.label}
                                                type={item.type}
                                                className="strong-text"
                                            />
                                        </strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                </div>
            </Offcanvas>
            {heroContent.length > 0 ? (
                <section className="uk-section-default">
                    <div className="uk-container uk-padding-remove">
                        <div className="uk-grid uk-grid-collapse uk-child-width-1-1@s  uk-child-width-1-1 uk-position-relative">
                            {heroContent.map((hero, index) => {
                                return (
                                    <div
                                        className={index === 0 ? null : "uk-visible@s"}
                                        style={{
                                            position: 'relative',
                                        }} key={hero.description}>
                                        <div className='uk-hidden@s'>
                                            <Image
                                                src="https://techno-images.s3.eu-central-1.amazonaws.com/standort-images/109/222/GFS+Header_OnlineShop_700+x+520+mobil_A+(1).png"
                                                alt={hero.headline}
                                            />
                                        </div>
                                        <div className='uk-visible@s'>
                                            <Image
                                                src={hero.imageLink}
                                                alt={hero.headline}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </section>
            ) : null}
        </>
    );
}

export default GottfriedHeader;
